import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cellCountForSize, hexPoints, sizeFromCellCount, SQRT3, v, type Axis } from '../constants';
import { computeConstraintStatuses } from '../rules';
import { HexBoard, HexCellBg, HexModifierBadge, HexRestrictionGlyph, HexTile } from './HexBoard';
import { Wordmark } from './ui';
import { checkCustomPuzzle, publishCustomPuzzle, type CheckResult, type PublishResult } from '../lib/customPuzzles';
import { BadgeAchievementModal } from './BadgeAchievementModal';
import { SharedHeader } from './SharedHeader';
import type { Badge } from '../lib/badges';
import { useAuth } from '../auth/AuthContext';
import type { Cell, Constraint, ConstraintType, Restriction } from '../types';

// Phase 4 — palette-based puzzle designer. Premium-gated.
//
// UX (rewritten 2026-05-11 from the dropdown-driven v1):
//   1. Pick grid size (Small / Medium / Large = sides 2 / 3 / 4).
//   2. Tap a palette tile to make it the active stamp, then tap board
//      cells to stamp them — OR drag a palette tile directly onto a
//      cell. Both gestures work.
//   3. The Locked palette tile has an inline stepper for picking the
//      value before stamping (range −9..9).
//   4. The "Empty" palette tile is the eraser.
//   5. Add line constraints from the inline editor at the bottom.
//   6. Name + Publish — server runs the solver, returns share URL.
//
// Why this UX vs the v1 dropdown-driven editor:
//   - Direct manipulation. You see what each stamp looks like before
//     applying. Dropdowns hide that.
//   - Mobile-friendlier — the v1 right-panel layout exploded on small
//     screens and the dropdowns required scroll-into-view fiddling.
//   - Fewer modes. v1 had a "selected cell" mode + per-field editors;
//     here every interaction is "stamp this cell with this kind."

const GRID_SIZES = [2, 3, 4] as const;
type GridSize = typeof GRID_SIZES[number];

// ─── Stamp catalogue ─────────────────────────────────────────────────

type Stamp =
  | { kind: 'empty' }
  | { kind: 'locked'; value: number }
  | { kind: 'gap' }
  | { kind: 'restriction'; restriction: Restriction }
  | { kind: 'modifier'; modifier: string };

interface PaletteEntry {
  id: string;
  label: string;
  stamp: Stamp;
}

const RESTRICTIONS: { value: Restriction; label: string }[] = [
  { value: 'even',  label: 'Even' },
  { value: 'odd',   label: 'Odd' },
  { value: 'pos',   label: 'Positive' },
  { value: 'neg',   label: 'Negative' },
  { value: 'prime', label: 'Prime' },
  { value: 'magHi', label: '|x|≥5' },
  { value: 'magLo', label: '|x|≤4' },
];

const MODIFIERS: string[] = [
  '*2', '*3', '+1', '+2', '+3', '+5', '-1', '-2', '-3', '*-1', '²', '³', '÷2', '÷3',
];

const DRAG_THRESHOLD = 5;

function stampToCell(stamp: Stamp): Cell {
  switch (stamp.kind) {
    case 'empty':    return { kind: 'empty' };
    case 'gap':      return { kind: 'gap' };
    case 'locked':   return { kind: 'locked', value: stamp.value };
    case 'restriction': return { kind: 'empty', _restriction: stamp.restriction };
    case 'modifier':    return { kind: 'empty', modifier: stamp.modifier };
  }
}

function emptyCells(size: GridSize): Cell[] {
  return Array.from({ length: cellCountForSize(size) }, () => ({ kind: 'empty' as const }));
}

function stampId(stamp: Stamp): string {
  switch (stamp.kind) {
    case 'empty':    return 'empty';
    case 'gap':      return 'gap';
    case 'locked':   return `locked:${stamp.value}`;
    case 'restriction': return `r:${stamp.restriction}`;
    case 'modifier':    return `m:${stamp.modifier}`;
  }
}

// ─── Component ──────────────────────────────────────────────────────

export function PuzzleDesigner() {
  const auth = useAuth();
  const [gridSize, setGridSize] = useState<GridSize>(2);
  const [cells, setCells] = useState<Cell[]>(() => emptyCells(2));
  const [constraints, setConstraints] = useState<Constraint[]>([]);
  const [name, setName] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<PublishResult | null>(null);
  // Achievement queue — populated when /custom returns newBadges on
  // publish (e.g. created_1 / created_10). Pops one at a time as the
  // user dismisses; rendered over the PublishResultModal.
  const [badgeQueue, setBadgeQueue] = useState<Badge[]>([]);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);

  // Palette state — the active stamp gets applied on cell taps, OR
  // gets attached to a drag operation when the user pointer-down's a
  // palette tile and moves the pointer.
  const [activeStamp, setActiveStamp] = useState<Stamp>({ kind: 'empty' });
  const [lockedValue, setLockedValue] = useState(1);

  // Lift body's overflow:hidden lock for the designer route so the
  // page scrolls naturally. Mirrors the legal-pages pattern.
  useEffect(() => {
    document.body.classList.add('hm-designer-open');
    return () => document.body.classList.remove('hm-designer-open');
  }, []);

  function handleSizeChange(s: GridSize) {
    if (s === gridSize) return;
    if (cells.some(c => c.kind !== 'empty') || constraints.length > 0) {
      if (!confirm('Changing grid size resets the current layout. Proceed?')) return;
    }
    setGridSize(s);
    setCells(emptyCells(s));
    setConstraints([]);
    setPublishResult(null);
  }

  function applyStamp(idx: number, stamp: Stamp) {
    setCells(prev => prev.map((c, i) => (i === idx ? stampToCell(stamp) : c)));
  }

  function addConstraint(c: Constraint) {
    setConstraints(prev => {
      const without = prev.filter(p => !(p.axis === c.axis && p.index === c.index));
      return [...without, c];
    });
  }
  function removeConstraint(axis: Axis, index: number) {
    setConstraints(prev => prev.filter(p => !(p.axis === axis && p.index === index)));
  }

  async function handlePublish() {
    setPublishing(true);
    setPublishResult(null);
    const result = await publishCustomPuzzle({
      name: name.trim(),
      cells,
      constraints,
    });
    if (result.ok && result.newBadges && result.newBadges.length > 0) {
      setBadgeQueue(prev => [...prev, ...(result.newBadges ?? [])]);
    }
    setPublishing(false);
    setPublishResult(result);
  }

  async function handleCheck() {
    setChecking(true);
    setCheckResult(null);
    const result = await checkCustomPuzzle({ cells, constraints });
    setChecking(false);
    setCheckResult(result);
  }

  // Any edit invalidates the previous check — clearing the banner makes
  // the user re-run after changing constraints / cells. Keep the banner
  // up while editing the name (name doesn't affect solvability).
  useEffect(() => {
    setCheckResult(null);
  }, [cells, constraints]);

  const evaluatedConstraints = useMemo(
    () => computeConstraintStatuses(cells, constraints),
    [cells, constraints],
  );

  // Constraint editor — opens when the user taps a `+` placeholder or
  // an existing chip on the board. Stores the (axis, index) being
  // edited; null = closed. Setting this also pins the line on the
  // board so the user sees exactly which cells the constraint covers.
  const [editingSlot, setEditingSlot] = useState<{ axis: Axis; index: number } | null>(null);
  const existingForSlot = editingSlot
    ? constraints.find(c => c.axis === editingSlot.axis && c.index === editingSlot.index) ?? null
    : null;

  // ─── Drag (palette → board) ────────────────────────────────────────
  const [drag, setDrag] = useState<{
    stamp: Stamp;
    startX: number; startY: number;
    x: number; y: number;
    active: boolean;
    overIdx: number | null;
  } | null>(null);

  const onPalettePointerDown = useCallback((stamp: Stamp, e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    setDrag({
      stamp,
      startX: e.clientX, startY: e.clientY,
      x: e.clientX, y: e.clientY,
      active: false,
      overIdx: null,
    });
  }, []);

  useEffect(() => {
    if (!drag) return;
    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const active = drag.active || dist > DRAG_THRESHOLD;
      let overIdx: number | null = null;
      if (active) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const cellEl = target && (target as Element).closest && (target as Element).closest('[data-cell-idx]');
        if (cellEl) {
          const idx = parseInt(cellEl.getAttribute('data-cell-idx')!, 10);
          if (!Number.isNaN(idx)) overIdx = idx;
        }
      }
      setDrag(d => d ? { ...d, x: e.clientX, y: e.clientY, active, overIdx } : null);
    };
    const onUp = (e: PointerEvent) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const wasDrag = drag.active || Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD;
      if (wasDrag) {
        const target = document.elementFromPoint(e.clientX, e.clientY);
        const cellEl = target && (target as Element).closest && (target as Element).closest('[data-cell-idx]');
        if (cellEl) {
          const idx = parseInt(cellEl.getAttribute('data-cell-idx')!, 10);
          if (!Number.isNaN(idx)) applyStamp(idx, drag.stamp);
        }
        // Suppress the synthetic click that follows pointerup so the
        // palette tile doesn't ALSO get a tap-select side-effect.
        const suppress = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
        document.addEventListener('click', suppress, { capture: true, once: true });
        setTimeout(() => document.removeEventListener('click', suppress, { capture: true }), 50);
      }
      // Below-threshold = tap on a palette tile. The tile's onClick
      // sets activeStamp; we don't apply anything to the board here.
      setDrag(null);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', () => setDrag(null));
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, [drag]);

  function handleCellTap(idx: number) {
    if (drag) return; // ignore taps during drag
    applyStamp(idx, activeStamp);
  }

  // ─── Premium / auth gate ──────────────────────────────────────────
  // The header still renders on the paywall surfaces so the user can
  // navigate to other parts of the site without going back to /.
  if (auth.loading) return (
    <div className="hm-designer">
      <SharedHeader activeView="design" />
      <CenteredMessage title="Loading…" />
    </div>
  );
  if (!auth.user) {
    return (
      <div className="hm-designer">
        <SharedHeader activeView="design" />
        <CenteredMessage
          title="Sign in to design puzzles"
          body="Hexponents Premium subscribers can author their own puzzles to share with friends."
          showHomeLink
        />
      </div>
    );
  }
  if (!auth.user.isPremium) {
    return (
      <div className="hm-designer">
        <SharedHeader activeView="design" />
        <CenteredMessage
          title="Design is a Premium feature"
          body="Authoring + sharing your own puzzles is part of Hexponents Premium. Anyone with the link can play — only authors need a subscription."
          showHomeLink
        />
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────
  const emptyCount = cells.filter(c => c.kind === 'empty').length;
  const lockedCount = cells.filter(c => c.kind === 'locked').length;
  const canPublish = !publishing && emptyCount > 0 && name.trim().length > 0;
  const activeId = stampId(activeStamp);

  return (
    <div className="hm-designer">
      <SharedHeader activeView="design" />

      {/* Body wrapper. The outer `.hm-designer` runs full-width so the
          header matches every other route on the site; the inner
          `.hm-designer-body` caps the editor surface at a centered
          column width so it doesn't read as edge-to-edge. */}
      <div className="hm-designer-body">

      {/* Name + Check + Publish moved to the top of the UI so the
          primary actions are always visible without scrolling.
          Stats + grid-size selector sit between this row and the
          board so the toolbar reads top-down: "what am I making →
          how big → make it". */}
      <div className="hm-designer-publish hm-designer-publish-top">
        <input
          type="text"
          className="hm-designer-name"
          placeholder="Name your puzzle"
          value={name}
          onChange={e => setName(e.target.value.slice(0, 80))}
          maxLength={80}
        />
        <button
          type="button"
          className="hm-action"
          disabled={checking || emptyCount === 0}
          onClick={handleCheck}
          title="Run the solver to verify the puzzle has a valid completion"
        >
          {checking ? 'Checking…' : 'Check'}
        </button>
        <button
          type="button"
          className="hm-primary"
          disabled={!canPublish}
          onClick={handlePublish}
        >
          {publishing ? 'Publishing…' : 'Publish'}
        </button>
      </div>

      {checkResult && (
        <CheckResultBanner result={checkResult} onDismiss={() => setCheckResult(null)} />
      )}

      <div className="hm-designer-stats">
        <SegSize value={gridSize} onChange={handleSizeChange} />
        <span className="hm-designer-stat">{emptyCount} empty</span>
        <span className="hm-designer-stat">{lockedCount} locked</span>
        <span className="hm-designer-stat">{constraints.length} constraint{constraints.length === 1 ? '' : 's'}</span>
      </div>

      {/* `hm-designer-main` is a layout-only wrapper. Portrait: it's
          `display: contents` so palette + board stack naturally with
          the rest. Landscape ≥720px: it becomes a 2-column grid
          (palette on the left, board on the right) using the full
          screen width. */}
      <div className="hm-designer-main">
        <PaletteSection
          activeStampId={activeId}
          lockedValue={lockedValue}
          setLockedValue={setLockedValue}
          onSelect={(stamp) => setActiveStamp(stamp)}
          onPaletteDragStart={onPalettePointerDown}
        />

        <DesignerBoardWrap
          cells={cells}
          evaluatedConstraints={evaluatedConstraints}
          onCellTap={handleCellTap}
          dragOverIdx={drag?.active ? drag.overIdx : null}
          editorSlots={true}
          onSlotClick={(axis, index) => setEditingSlot({ axis, index })}
          forcedPinnedLine={editingSlot}
        />
      </div>

      <div className="hm-designer-constraints-hint">
        Tap a <span className="hm-chip-plus-inline">+</span> on the board to add a line constraint.
        Constraints define the rule each line must satisfy.
      </div>

      {/* Drag ghost — only visible during an active drag */}
      {drag && drag.active && (
        <div
          className="hm-designer-ghost"
          style={{
            transform: `translate3d(${drag.x}px, ${drag.y}px, 0) translate(-50%, -50%)`,
          }}
        >
          <StampPreview stamp={drag.stamp} R={22} />
        </div>
      )}

      {editingSlot && (
        <ConstraintPickerModal
          slot={editingSlot}
          existing={existingForSlot}
          onApply={(c) => { addConstraint(c); setEditingSlot(null); }}
          onRemove={() => {
            removeConstraint(editingSlot.axis, editingSlot.index);
            setEditingSlot(null);
          }}
          onClose={() => setEditingSlot(null)}
        />
      )}

      {publishResult && (
        <PublishResultModal result={publishResult} onClose={() => setPublishResult(null)} />
      )}

      {badgeQueue.length > 0 && (
        <BadgeAchievementModal
          badge={badgeQueue[0]}
          remaining={badgeQueue.length - 1}
          onDismiss={() => setBadgeQueue(prev => prev.slice(1))}
        />
      )}
      </div>
    </div>
  );
}

// ─── Grid size segmented ─────────────────────────────────────────────

function SegSize({ value, onChange }: { value: GridSize; onChange: (s: GridSize) => void }) {
  return (
    <div className="hm-designer-seg" role="tablist" aria-label="Grid size">
      {GRID_SIZES.map(s => (
        <button
          key={s}
          role="tab"
          aria-selected={value === s}
          className={value === s ? 'active' : ''}
          onClick={() => onChange(s)}
        >
          {s === 2 ? 'Small' : s === 3 ? 'Medium' : 'Large'}
        </button>
      ))}
    </div>
  );
}

// ─── Palette ────────────────────────────────────────────────────────

interface PaletteSectionProps {
  activeStampId: string;
  lockedValue: number;
  setLockedValue: (n: number) => void;
  onSelect: (stamp: Stamp) => void;
  onPaletteDragStart: (stamp: Stamp, e: React.PointerEvent) => void;
}

function PaletteSection({
  activeStampId, lockedValue, setLockedValue, onSelect, onPaletteDragStart,
}: PaletteSectionProps) {
  // The Locked palette tile carries the current lockedValue; changing
  // the stepper also updates the activeStamp if Locked is selected.
  function pickLocked(stamp: Stamp) {
    onSelect(stamp);
  }

  return (
    <div className="hm-designer-palette">
      <div className="hm-designer-palette-hint">
        Tap a tile to select, then tap board cells to stamp. Or drag a tile onto a cell.
      </div>

      <PaletteRow title="Cells">
        <PaletteTile
          stamp={{ kind: 'empty' }}
          active={activeStampId === 'empty'}
          onSelect={onSelect}
          onDragStart={onPaletteDragStart}
          label="Empty"
        />
        <PaletteTileLocked
          value={lockedValue}
          setValue={(v) => {
            setLockedValue(v);
            if (activeStampId.startsWith('locked:')) pickLocked({ kind: 'locked', value: v });
          }}
          active={activeStampId === `locked:${lockedValue}`}
          onSelect={() => onSelect({ kind: 'locked', value: lockedValue })}
          onDragStart={(e) => onPaletteDragStart({ kind: 'locked', value: lockedValue }, e)}
        />
        <PaletteTile
          stamp={{ kind: 'gap' }}
          active={activeStampId === 'gap'}
          onSelect={onSelect}
          onDragStart={onPaletteDragStart}
          label="Gap"
        />
      </PaletteRow>

      <PaletteRow title="Restrictions">
        {RESTRICTIONS.map(r => (
          <PaletteTile
            key={r.value}
            stamp={{ kind: 'restriction', restriction: r.value }}
            active={activeStampId === `r:${r.value}`}
            onSelect={onSelect}
            onDragStart={onPaletteDragStart}
            label={r.label}
          />
        ))}
      </PaletteRow>

      <PaletteRow title="Modifiers">
        {MODIFIERS.map(m => (
          <PaletteTile
            key={m}
            stamp={{ kind: 'modifier', modifier: m }}
            active={activeStampId === `m:${m}`}
            onSelect={onSelect}
            onDragStart={onPaletteDragStart}
            label={m}
          />
        ))}
      </PaletteRow>
    </div>
  );
}

function PaletteRow({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="hm-palette-row">
      <div className="hm-palette-row-title">{title}</div>
      <div className="hm-palette-row-tiles">{children}</div>
    </div>
  );
}

interface PaletteTileProps {
  stamp: Stamp;
  active: boolean;
  onSelect: (stamp: Stamp) => void;
  onDragStart: (stamp: Stamp, e: React.PointerEvent) => void;
  label: string;
}

function PaletteTile({ stamp, active, onSelect, onDragStart, label }: PaletteTileProps) {
  return (
    <button
      type="button"
      className={`hm-palette-tile ${active ? 'active' : ''}`}
      onClick={() => onSelect(stamp)}
      onPointerDown={(e) => onDragStart(stamp, e)}
      title={label}
      aria-pressed={active}
    >
      <StampPreview stamp={stamp} R={20} />
      <span className="hm-palette-tile-label">{label}</span>
    </button>
  );
}

function PaletteTileLocked({
  value, setValue, active, onSelect, onDragStart,
}: {
  value: number;
  setValue: (n: number) => void;
  active: boolean;
  onSelect: () => void;
  onDragStart: (e: React.PointerEvent) => void;
}) {
  return (
    <div className={`hm-palette-tile hm-palette-tile-locked ${active ? 'active' : ''}`}>
      <div
        className="hm-palette-tile-body"
        onClick={onSelect}
        onPointerDown={onDragStart}
        role="button"
        aria-pressed={active}
        title={`Locked ${value}`}
      >
        <StampPreview stamp={{ kind: 'locked', value }} R={20} />
        <span className="hm-palette-tile-label">Locked</span>
      </div>
      <div className="hm-palette-tile-stepper" aria-label="Locked value">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setValue(Math.max(-9, value - 1)); }}
          aria-label="Decrease value"
        >−</button>
        <span>{value}</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setValue(Math.min(9, value + 1)); }}
          aria-label="Increase value"
        >+</button>
      </div>
    </div>
  );
}

// Renders a small inline SVG of a single cell as it would appear on
// the board. Uses the same primitives HexBoard renders so the palette
// previews are pixel-faithful.
function StampPreview({ stamp, R = 20 }: { stamp: Stamp; R?: number }) {
  const w = R * 2 + 6;
  const h = R * SQRT3 + 6;

  // HexCellBg expects 'empty' | 'restricted' | 'modifier' | 'invisible' —
  // for locked + gap we render the appropriate primitive directly.
  let background: 'empty' | 'restricted' | 'modifier' | null = null;
  let restriction: Restriction | undefined = undefined;
  let modifierLabel: string | null = null;
  let lockedValue: number | null = null;
  let isGap = false;

  switch (stamp.kind) {
    case 'empty':
      background = 'empty';
      break;
    case 'gap':
      isGap = true;
      break;
    case 'locked':
      lockedValue = stamp.value;
      break;
    case 'restriction':
      background = 'restricted';
      restriction = stamp.restriction;
      break;
    case 'modifier':
      background = 'modifier';
      modifierLabel = stamp.modifier;
      break;
  }

  return (
    <svg width={w} height={h} viewBox={`-${w/2} -${h/2} ${w} ${h}`} className="hm-stamp-svg">
      {isGap && (
        <polygon
          points={hexPoints(0, 0, R * 0.55)}
          fill="none"
          stroke={v('cellEmptyStroke')}
          strokeWidth={1.25}
          strokeDasharray="3 3"
          strokeLinejoin="round"
        />
      )}
      {background && (
        <HexCellBg cx={0} cy={0} R={R} kind={background} restriction={restriction} />
      )}
      {restriction && (
        <HexRestrictionGlyph cx={0} cy={0} R={R} kind={restriction} />
      )}
      {modifierLabel && (
        <HexModifierBadge cx={0} cy={0} R={R} label={modifierLabel} />
      )}
      {lockedValue != null && (
        <HexTile cx={0} cy={0} R={R} value={lockedValue} locked={true} />
      )}
    </svg>
  );
}

// ─── Board ─────────────────────────────────────────────────────────

function DesignerBoardWrap({
  cells, evaluatedConstraints, onCellTap, dragOverIdx,
  editorSlots, onSlotClick, forcedPinnedLine,
}: {
  cells: Cell[];
  evaluatedConstraints: ReturnType<typeof computeConstraintStatuses>;
  onCellTap: (idx: number) => void;
  dragOverIdx: number | null;
  editorSlots?: boolean;
  onSlotClick?: (axis: Axis, index: number) => void;
  forcedPinnedLine?: { axis: Axis; index: number } | null;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [boardBox, setBoardBox] = useState({ w: 480, h: 320 });
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const onR = () => setVw(window.innerWidth);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  useEffect(() => {
    if (!wrapRef.current || typeof ResizeObserver === 'undefined') return;
    const el = wrapRef.current;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) setBoardBox({ w: width, h: height });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { R, pad } = useMemo(() => {
    const size = sizeFromCellCount(cells.length);
    const numColsM1 = 2 * size - 2;
    const widthCoef = 3 * size - 1;
    const heightCoef = (2 * size - 1) * SQRT3;
    const minR = vw < 480 ? 18 : 24;
    // Cap grows with viewport width — the original 44px cap was
    // designed for the old 380px-tall portrait board. The landscape
    // split gives us a much taller canvas and the cap was leaving
    // the board mostly empty. Cells stay tighter on size-4 because
    // there's just more of them sharing the space.
    const maxR =
      vw < 480 ? 32
      : vw < 720 ? 44
      : vw < 1100 ? 64
      : 88;
    const padW = vw < 480 ? 56 : 72;
    const padH = vw < 480 ? 28 : 40;
    const gap = 5;
    const Rw = (boardBox.w - 2 * padW - numColsM1 * gap) / widthCoef;
    const Rh = (boardBox.h - 2 * padH - numColsM1 * gap) / heightCoef;
    let R = Math.min(Rw, Rh, maxR);
    if (!Number.isFinite(R) || R < minR) R = minR;
    return { R, pad: Math.max(padH, padW) };
  }, [boardBox, vw, cells.length]);

  const clickableCells = useMemo(() => {
    const set = new Set<number>();
    cells.forEach((_, i) => set.add(i));
    return set;
  }, [cells]);

  return (
    <div className="hm-designer-boardwrap" ref={wrapRef}>
      <HexBoard
        cells={cells}
        evaluatedConstraints={evaluatedConstraints}
        badRestr={new Set()}
        R={R}
        pad={pad}
        gap={5}
        onCellClick={onCellTap}
        onCellPointerDown={() => {/* no cell-side drag in editor */}}
        clickableCells={clickableCells}
        draggableCells={new Set()}
        recentlyPlaced={null}
        drag={null}
        dragOverIdx={dragOverIdx}
        editorSlots={editorSlots}
        onSlotClick={onSlotClick}
        forcedPinnedLine={forcedPinnedLine}
      />
    </div>
  );
}

// ─── Constraint picker modal ──────────────────────────────────────
//
// Opens when the user taps a `+` slot on the board (or an existing
// chip in editor mode). Type tiles instead of a dropdown — same
// abbreviated symbols you see on the chips (`Σ`, `×`, `≥`, etc.) so
// the user picks visually. Value field appears only for types that
// take one.

interface ConstraintTypeMeta {
  value: ConstraintType;
  tag: string;
  label: string;
  description: string;
  needsValue: boolean;
  needsArray?: boolean;
}

const CONSTRAINT_PICKER_TYPES: ConstraintTypeMeta[] = [
  { value: 'sum',      tag: 'Σ',        label: 'Sum equals',   description: 'Tile values add to N',           needsValue: true },
  { value: 'product',  tag: '×',        label: 'Product',      description: 'Tile values multiply to N',      needsValue: true },
  { value: 'sumGte',   tag: '≥',        label: 'Sum ≥',        description: 'Sum is at least N',              needsValue: true },
  { value: 'sumLte',   tag: '≤',        label: 'Sum ≤',        description: 'Sum is at most N',               needsValue: true },
  { value: 'allEven',  tag: 'Even',     label: 'All even',     description: 'Every tile is even',             needsValue: false },
  { value: 'allOdd',   tag: 'Odd',      label: 'All odd',      description: 'Every tile is odd',              needsValue: false },
  { value: 'noNeg',    tag: 'Pos.',     label: 'No negatives', description: 'Every tile is ≥ 0',              needsValue: false },
  { value: 'allNeg',   tag: 'All Neg.', label: 'All negative', description: 'Every tile is < 0',              needsValue: false },
  { value: 'negCount', tag: 'N Neg.',   label: 'N negatives',  description: 'Exactly N negatives on the line', needsValue: true },
  { value: 'contains', tag: '{}',       label: 'Contains',     description: 'These specific values appear',   needsValue: true, needsArray: true },
];

function ConstraintPickerModal({
  slot, existing, onApply, onRemove, onClose,
}: {
  slot: { axis: Axis; index: number };
  existing: Constraint | null;
  onApply: (c: Constraint) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const [type, setType] = useState<ConstraintType>(existing?.type ?? 'sum');
  const [valueStr, setValueStr] = useState(() => {
    if (!existing?.value) return '';
    if (Array.isArray(existing.value)) return existing.value.join(', ');
    return String(existing.value);
  });

  const meta = CONSTRAINT_PICKER_TYPES.find(t => t.value === type)!;

  // Esc to close, matching the rest of the modal stack.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function commit() {
    let value: number | number[] | undefined;
    if (meta.needsValue) {
      if (meta.needsArray) {
        const parts = valueStr.split(',').map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n));
        if (!parts.length) { alert('Enter at least one value, comma-separated.'); return; }
        value = parts;
      } else {
        const n = parseInt(valueStr, 10);
        if (!Number.isFinite(n)) { alert('Enter a number.'); return; }
        value = n;
      }
    }
    onApply({ axis: slot.axis, index: slot.index, type, value });
  }

  return (
    <div className="hm-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Edit constraint">
      <div className="hm-modal hm-constraint-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="hm-modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2 style={{ marginBottom: 4 }}>
          {existing ? 'Edit constraint' : 'Add constraint'}
        </h2>
        <p className="hm-constraint-modal-line">
          {axisLabel(slot.axis)} · Line {slot.index}
        </p>

        <div className="hm-constraint-type-grid">
          {CONSTRAINT_PICKER_TYPES.map(t => (
            <button
              key={t.value}
              type="button"
              className={`hm-constraint-type-tile ${type === t.value ? 'active' : ''}`}
              onClick={() => setType(t.value)}
              aria-pressed={type === t.value}
              title={t.description}
            >
              <span className="hm-constraint-type-tag">{t.tag}</span>
              <span className="hm-constraint-type-label">{t.label}</span>
            </button>
          ))}
        </div>

        {meta.needsValue && (
          <div className="hm-constraint-value-row">
            <label className="hm-constraint-value-label">
              {meta.needsArray ? 'Values' : 'Value'}
            </label>
            <input
              type="text"
              className="hm-designer-input"
              value={valueStr}
              onChange={e => setValueStr(e.target.value)}
              placeholder={meta.needsArray ? 'e.g. 2, 3, 5' : 'e.g. 14'}
              autoFocus
            />
          </div>
        )}

        <p className="hm-constraint-modal-desc">{meta.description}</p>

        <div className="hm-constraint-modal-actions">
          {existing && (
            <button type="button" className="hm-action hm-action-danger" onClick={onRemove}>
              Remove
            </button>
          )}
          <button type="button" className="hm-action" onClick={onClose}>Cancel</button>
          <button type="button" className="hm-primary" onClick={commit}>
            {existing ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

function axisLabel(a: Axis): string {
  return a === 'col' ? 'Column' : a === 'nwse' ? 'NW→SE' : 'NE→SW';
}

// ─── Check-result banner ──────────────────────────────────────────
//
// Inline above the publish footer. Green tick on a solvable layout,
// red on unsolvable + the server's hint message. Dismissed
// automatically when the user edits the layout (cells/constraints
// change → useEffect clears the result).

function CheckResultBanner({
  result, onDismiss,
}: {
  result: CheckResult;
  onDismiss: () => void;
}) {
  return (
    <div className={`hm-check-banner ${result.ok ? 'ok' : 'bad'}`} role="status">
      <div className="hm-check-banner-icon" aria-hidden="true">
        {result.ok ? '✓' : '⚠'}
      </div>
      <div className="hm-check-banner-text">
        {result.ok ? (
          <>
            <strong>Solvable.</strong>{' '}
            {typeof result.bagSize === 'number'
              ? `Solver found a valid bag with ${result.bagSize} tile${result.bagSize === 1 ? '' : 's'}.`
              : 'Solver found a valid completion.'}{' '}
            Click Publish when ready.
          </>
        ) : (
          <>
            <strong>Couldn't solve.</strong>{' '}
            {result.message ?? messageForError(result.error)}
          </>
        )}
      </div>
      <button type="button" className="hm-modal-close hm-check-banner-close" onClick={onDismiss} aria-label="Dismiss">×</button>
    </div>
  );
}

// ─── Publish result + paywall (reused from v1) ────────────────────

function PublishResultModal({ result, onClose }: { result: PublishResult; onClose: () => void }) {
  const shareUrl = result.url ? `${window.location.origin}${result.url}` : '';
  return (
    <div className="hm-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="hm-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="hm-modal-close" onClick={onClose} aria-label="Close">×</button>
        {result.ok ? (
          <>
            <h2>Published.</h2>
            <p style={{ fontSize: 13, color: v('inkMute') }}>Share this URL — anyone with the link can play.</p>
            <div className="hm-designer-share">
              <input type="text" readOnly value={shareUrl} onFocus={e => e.target.select()} />
              <button
                type="button"
                className="hm-primary"
                onClick={() => navigator.clipboard?.writeText(shareUrl)}
              >Copy</button>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <a href={result.url} className="hm-action" style={{ textDecoration: 'none' }}>Play it now</a>
              <button type="button" className="hm-primary" onClick={onClose}>Design another</button>
            </div>
          </>
        ) : (
          <>
            <h2>Couldn't publish.</h2>
            <p style={{ fontSize: 14, color: v('inkSoft'), marginTop: 8 }}>
              {result.message ?? messageForError(result.error)}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button type="button" className="hm-primary" onClick={onClose}>Back to designer</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function messageForError(error?: string): string {
  switch (error) {
    case 'unsolvable':       return 'This map has no valid completion in the −9 to 9 range. Loosen a constraint or open up a restricted cell.';
    case 'timeout':          return 'Solver hit its time budget before finding a valid completion. The layout may still be solvable — try simplifying a constraint, reducing the number of empty cells, or loosening a restriction.';
    case 'premium_required': return 'Authoring is a Premium feature. Please upgrade to publish puzzles.';
    case 'unauthenticated':  return 'Your session expired. Sign in again and retry.';
    case 'invalid_grid_size':return 'The grid size isn\'t supported. Pick Small, Medium, or Large.';
    case 'no_playable_cells':return 'The puzzle needs at least one empty cell for the player to fill.';
    case 'invalid_input':    return 'One or more fields don\'t look right. Check cell values and constraint inputs.';
    case 'inappropriate_name': return 'Please choose a different puzzle name.';
    case 'network':          return 'Network error. Check your connection and retry.';
    default:                 return 'Something went wrong. Please try again.';
  }
}

function CenteredMessage({
  title, body, showHomeLink,
}: {
  title: string;
  body?: string;
  showHomeLink?: boolean;
}) {
  return (
    <div
      style={{
        minHeight: '100dvh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '32px 24px', textAlign: 'center', gap: 14,
      }}
    >
      <div style={{ marginBottom: 8 }}><Wordmark /></div>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{title}</h1>
      {body && <p style={{ margin: 0, maxWidth: 420, color: v('inkSoft'), fontSize: 14 }}>{body}</p>}
      {showHomeLink && (
        <a href="/" className="hm-primary" style={{ marginTop: 12, padding: '8px 18px', textDecoration: 'none' }}>
          Open Hexponents
        </a>
      )}
    </div>
  );
}
